import BigNumber from "bignumber.js"
import { Info } from "lucide-react"
import moment from "moment"
import { useEffect, useId, useRef, useState } from "react"
import { average as findAverage } from "simple-statistics"
import { NearbyContributor, VesselLeg } from "../hooks/usePrevetAPI"
import styles from "./PrevetZoneAvgPopover.module.scss"

function contributors(value: unknown): NearbyContributor[] | null {
  if (!Array.isArray(value) || !value.every((row) =>
    row != null && Number.isInteger(row.imo) &&
    typeof row.vessel_name === "string" && Number.isFinite(row.sog) &&
    Number.isFinite(row.seamargin_sog)
  )) return null
  return [...value].sort((a, b) =>
    a.vessel_name.localeCompare(b.vessel_name, "en") || a.imo - b.imo
  )
}

interface PrevetZoneAvgPopoverProps {
  speedData: VesselLeg[]
  vesselName: string
  visibleStartIndex: number
  visibleEndIndex: number
  /** Called when a date row is chosen, e.g. to open the trade map on that leg. */
  onSelectLeg?: (leg: VesselLeg) => void
}

export function PrevetZoneAvgPopover({
  speedData,
  vesselName,
  visibleStartIndex,
  visibleEndIndex,
  onSelectLeg,
}: PrevetZoneAvgPopoverProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()

  const visibleData = speedData.slice(
    visibleStartIndex,
    visibleEndIndex + 1
  )

  const zoneAvgDates = visibleData
    .filter((leg) => leg.zone_avg_sog != null)
    .map((leg) => ({
      leg,
      date: leg.pos_date,
      zone_avg_sog: leg.zone_avg_sog!,
      zone_avg_sm_sog: leg.zone_avg_sm_sog ?? 0,
      day_sm_avg: leg.zone_avg_sm_sog,
      laden: leg.laden,
      ships: contributors(leg.nearby_contributors),
    }))

  const rowsNewestFirst = [...zoneAvgDates].sort(
    (a, b) => moment(b.date).valueOf() - moment(a.date).valueOf()
  )

  const zoneAvgValues = zoneAvgDates.map((d) => d.zone_avg_sog)
  const overallAvg =
    zoneAvgValues.length > 0
      ? BigNumber(findAverage(zoneAvgValues)).toFixed(1)
      : null

  const overallSmAvg =
    zoneAvgDates.length > 0
      ? BigNumber(findAverage(zoneAvgDates.map((d) => d.zone_avg_sm_sog))).toFixed(1)
      : null

  useEffect(() => {
    if (!isOpen) return
    const dismiss = (e: Event) => {
      if (!popoverRef.current?.contains(e.target as Node)) {
        if (popoverRef.current?.contains(document.activeElement)) buttonRef.current?.focus()
        setIsOpen(false)
        setPinned(false)
      }
    }
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (popoverRef.current?.contains(document.activeElement)) buttonRef.current?.focus()
        setIsOpen(false)
        setPinned(false)
      }
    }
    document.addEventListener("pointerdown", dismiss)
    document.addEventListener("focusin", dismiss)
    document.addEventListener("keydown", escape)
    return () => {
      document.removeEventListener("pointerdown", dismiss)
      document.removeEventListener("focusin", dismiss)
      document.removeEventListener("keydown", escape)
    }
  }, [isOpen])

  if (overallAvg == null) return null

  const selectLeg = (leg: VesselLeg) => {
    setIsOpen(false)
    setPinned(false)
    onSelectLeg?.(leg)
  }

  return (
    <div
      ref={popoverRef}
      className={isOpen ? `${styles.root} ${styles.open}` : styles.root}
      onMouseLeave={() => {
        if (!pinned && !popoverRef.current?.contains(document.activeElement)) setIsOpen(false)
      }}
    >
      <div className={styles.label}>
        <span className={styles.metric}>
          Nearby Vessels avg: <strong>{overallAvg}</strong>
        </span>
        {" "}
        <span className={styles.metric}>
          SM avg: <strong>{overallSmAvg}</strong>
        </span>
        <button
          ref={buttonRef}
          type="button"
          className={styles.info}
          aria-label="Nearby vessel contributors"
          aria-expanded={isOpen}
          aria-controls={panelId}
          onMouseEnter={() => setIsOpen(true)}
          onClick={() => { setPinned(!pinned); setIsOpen(!pinned) }}
        >
          <Info size={14} strokeWidth={2.25} aria-hidden="true" />
        </button>
      </div>

      {isOpen && (
        <div
          id={panelId}
          role="region"
          aria-label="Nearby vessel contributor details"
          tabIndex={0}
          className={styles.panel}
        >
          <div className={styles.summary}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>
              Similar vessels on same trade break down:
            </div>
            <div style={{ color: "#666" }}>
              Avg: <strong>{overallAvg}</strong> kt &nbsp;&nbsp;SM avg:{" "}
              <strong>{overallSmAvg}</strong> kt &nbsp;·&nbsp;{" "}
              {zoneAvgDates.length} date
              {zoneAvgDates.length !== 1 ? "s" : ""} with similar vessels on same trade
            </div>
          </div>
          <div className={styles.body}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th
                    className={styles.tableHeading}
                    style={{
                      padding: "8px 14px",
                      textAlign: "left",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Date
                  </th>
                  <th
                    className={styles.tableHeading}
                    style={{
                      padding: "8px 14px",
                      textAlign: "right",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Avg (kt)
                  </th>
                  <th
                    className={styles.tableHeading}
                    style={{
                      padding: "8px 14px",
                      textAlign: "right",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    SM avg (kt)
                  </th>
                  <th
                    className={styles.tableHeading}
                    style={{
                      padding: "8px 14px",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Name of similar vessels nearby {vesselName}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rowsNewestFirst.map(({ leg, date, laden, zone_avg_sog, day_sm_avg, ships }) => (
                  <tr
                    key={`${date}-${laden}`}
                    className={onSelectLeg ? styles.selectable : undefined}
                    tabIndex={onSelectLeg ? 0 : undefined}
                    title={onSelectLeg ? "Open trade map for this date" : undefined}
                    onClick={onSelectLeg ? () => selectLeg(leg) : undefined}
                    onKeyDown={
                      onSelectLeg
                        ? (e) => {
                            if (e.key !== "Enter" && e.key !== " ") return
                            e.preventDefault()
                            selectLeg(leg)
                          }
                        : undefined
                    }
                  >
                    <td
                      style={{
                        padding: "8px 14px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {moment(date).format("DD-MM-YYYY")}
                    </td>
                    <td
                      style={{
                        padding: "8px 14px",
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {BigNumber(zone_avg_sog).toFixed(1)}
                    </td>
                    <td
                      style={{
                        padding: "8px 14px",
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {day_sm_avg != null ? BigNumber(day_sm_avg).toFixed(1) : "–"}
                    </td>
                    <td
                      className={styles.contributors}
                      style={{
                        padding: "8px 14px",
                        color: "#666",
                      }}
                    >
                      {ships === null ? (
                        <span>Contributor details unavailable</span>
                      ) : ships.length === 0 ? (
                        <span>No ships found</span>
                      ) : ships.map((ship) => (
                        <div key={ship.imo} className={styles.ship}>
                          <strong>{ship.vessel_name}</strong>
                          {` (avg: ${ship.sog.toFixed(1)} kt, sm avg: ${ship.seamargin_sog.toFixed(1)} kt)`}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
